/**
 * 导出模态框组件
 * 提供导出选项和配置界面
 */

import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { ExportOptionGrid } from './ExportOption'
import { LoadingOverlay } from './LoadingOverlay'
import { ExportFormat, ExportOptions } from '../../types/export'
import { generateDefaultDocumentName } from '../../utils/time'

/**
 * 检测 Markdown 内容中是否包含表格
 * Markdown 表格格式: | header | header | 后跟 |---|---|
 */
const hasMarkdownTable = (content: string): boolean => {
  if (!content) return false
  // 匹配 Markdown 表格: 至少有一行包含 | 分隔符，且下一行是分隔行 |---|
  const tablePattern = /\|.+\|[\r\n]+\|[\s:-]+\|/
  return tablePattern.test(content)
}

interface ExportModalProps {
  isVisible: boolean
  onClose: () => void
  onExport: (options: ExportOptions) => Promise<void>
  documentTitle?: string
  content?: string
  isExporting?: boolean
  exportProgress?: number
  onCancel?: () => void
}

/**
 * 导出模态框组件
 * 提供完整的导出流程界面
 */
export const ExportModal: React.FC<ExportModalProps> = ({
  isVisible,
  onClose,
  onExport,
  documentTitle,
  content = '',
  isExporting = false,
  exportProgress = 0,
  onCancel
}) => {
  const { t } = useTranslation()
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat | null>(null)

  // 检测内容中是否有表格，决定是否禁用 Excel/CSV 导出
  const disabledFormats = useMemo<ExportFormat[]>(() => {
    if (!hasMarkdownTable(content)) {
      return ['excel', 'csv']
    }
    return []
  }, [content])
  const [filename, setFilename] = useState('')
  const [isFilenameModified, setIsFilenameModified] = useState(false)
  const [defaultFilename, setDefaultFilename] = useState('')
  const [includeStyles, setIncludeStyles] = useState(true)
  const [includeMath, setIncludeMath] = useState(true)
  const [includeCode, setIncludeCode] = useState(true)
  const [pageSize, setPageSize] = useState<'A4' | 'A3' | 'Letter'>('A4')
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait')

  const handleFormatSelect = (format: ExportFormat | null) => {
    setSelectedFormat(format)
    if (format) {
      const extensions: Record<ExportFormat, string> = {
        pdf: 'pdf',
        word: 'docx',
        html: 'html',
        png: 'png',
        jpeg: 'jpeg',
        svg: 'svg',
        excel: 'xlsx',
        csv: 'csv',
        markdown: 'md',
        rtf: 'rtf'
      }
      const baseTitle = generateDefaultDocumentName()
      setDefaultFilename(`${baseTitle}.${extensions[format]}`)
      setIsFilenameModified(false)
      setFilename('')
    }
  }

  // 重置表单
  const resetForm = () => {
    setSelectedFormat(null)
    setFilename('')
    setIsFilenameModified(false)
    setDefaultFilename('')
    setIncludeStyles(true)
    setIncludeMath(true)
    setIncludeCode(true)
    setPageSize('A4')
    setOrientation('portrait')
  }

  // 处理关闭
  const handleClose = () => {
    if (!isExporting) {
      resetForm()
      onClose()
    }
  }

  // 处理文件名变化
  const handleFilenameChange = (value: string) => {
    setFilename(value)
    if (value.trim() === '') {
      // 清空时重置为占位符状态
      setIsFilenameModified(false)
    } else {
      setIsFilenameModified(true)
    }
  }

  // 处理导出
  const handleExport = async () => {
    if (!selectedFormat) return
    
    const exportOptions: ExportOptions = {
      format: selectedFormat,
      filename: isFilenameModified && filename.trim() ? filename : defaultFilename,
      title: documentTitle || undefined,
      includeStyles,
      includeMath,
      includeCode,
      pageSize,
      orientation
    }

    try {
      await onExport(exportOptions)
      resetForm()
      onClose()
    } catch (error) {
      console.error('导出失败:', error)
    }
  }

  // 根据格式显示相应的配置选项
  const renderFormatOptions = () => {
    if (!selectedFormat) return null

    return (
      <div className="space-y-4">
        {/* 文件名输入 */}
        <div>
          <label htmlFor="export-filename" className="block text-sm font-medium text-gray-700 mb-2">
            {t('export.modal.filename')}
          </label>
          <Input
            id="export-filename"
            name="filename"
            value={filename}
            onChange={(e) => handleFilenameChange(e.target.value)}
            placeholder={defaultFilename}
            disabled={isExporting}
          />
        </div>

        {/* PDF 特定选项 */}
        {(selectedFormat === 'pdf') && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="pdf-page-size" className="block text-sm font-medium text-gray-700 mb-2">
                {t('export.modal.pageSize')}
              </label>
              <select
                id="pdf-page-size"
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value as 'A4' | 'A3' | 'Letter')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isExporting}
              >
                <option value="A4">A4</option>
                <option value="A3">A3</option>
                <option value="Letter">Letter</option>
              </select>
            </div>
            <div>
              <label htmlFor="pdf-orientation" className="block text-sm font-medium text-gray-700 mb-2">
                {t('export.modal.orientation')}
              </label>
              <select
                id="pdf-orientation"
                value={orientation}
                onChange={(e) => setOrientation(e.target.value as 'portrait' | 'landscape')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isExporting}
              >
                <option value="portrait">{t('export.modal.portrait')}</option>
                <option value="landscape">{t('export.modal.landscape')}</option>
              </select>
            </div>
          </div>
        )}

        {/* 内容选项 */}
        <fieldset>
          <legend className="block text-sm font-medium text-gray-700 mb-2">
            {t('export.modal.content')}
          </legend>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeStyles}
                onChange={(e) => setIncludeStyles(e.target.checked)}
                disabled={isExporting}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                {t('export.modal.includeStyles')}
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeMath}
                onChange={(e) => setIncludeMath(e.target.checked)}
                disabled={isExporting}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                {t('export.modal.includeMath')}
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeCode}
                onChange={(e) => setIncludeCode(e.target.checked)}
                disabled={isExporting}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                {t('export.modal.includeCode')}
              </span>
            </label>
          </div>
        </fieldset>
      </div>
    )
  }

  return (
    <>
      <Modal
        isOpen={isVisible}
        onClose={handleClose}
        title={t('export.modal.title')}
        size="lg"
        footer={
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isExporting}
            >
              {t('export.modal.cancel')}
            </Button>
            <Button
              onClick={handleExport}
              disabled={!selectedFormat || isExporting}
              loading={isExporting}
            >
              {isExporting ? t('export.modal.exporting') : t('export.modal.startExport')}
            </Button>
          </div>
        }
      >
        <div className="space-y-6">
          {/* 格式选择 */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('export.modal.selectFormat')}
            </h3>
            <ExportOptionGrid
              selectedFormat={selectedFormat || undefined}
              onFormatSelect={handleFormatSelect}
              disabledFormats={disabledFormats}
            />
          </div>

          {/* 格式特定选项 */}
          {selectedFormat && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('export.modal.options')}
              </h3>
              {renderFormatOptions()}
            </div>
          )}
        </div>
      </Modal>

      {/* 加载遮罩 */}
      <LoadingOverlay
        isVisible={isExporting}
        progress={exportProgress}
        message={t('export.modal.exporting')}
        onCancel={onCancel}
      />
    </>
  )
}